import { useState } from 'react'
import { useFileStore } from '../stores/fileStore'
import { useAuthStore } from '../stores/authStore'
import { useToastStore } from '../stores/toastStore'
import { X, Folder, FolderOpen, Lock, Users } from 'lucide-react'
import { HOME_URL } from '../config'

interface CreateFolderModalProps {
  isOpen: boolean
  onClose: () => void
  currentPath: string
}

export function CreateFolderModal({ isOpen, onClose, currentPath }: CreateFolderModalProps) {
  const [folderName, setFolderName] = useState('')
  const [isPublic, setIsPublic] = useState(true)
  const [isShared, setIsShared] = useState(false)
  const [password, setPassword] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const { createFolder, loadItems } = useFileStore()
  const { token, csrfToken } = useAuthStore()
  const { addToast } = useToastStore()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!folderName.trim()) return

    setIsCreating(true)
    try {
      if (isShared && password) {
        // Step 1: Create shared folder entry in database
        const sharedResponse = await fetch('/api/shared-folders/create', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-CSRF-Token': csrfToken || '',
          },
          body: JSON.stringify({
            folderName: folderName.trim(),
            password: password
          })
        })

        if (sharedResponse.ok) {
          // Step 2: Also create the actual folder in file system
          await createFolder(folderName.trim(), isPublic)
          
          addToast({
            type: 'success',
            message: `Shared folder created! URL: ${window.location.origin}/${folderName}`
          })

          // Copy URL to clipboard
          try {
            await navigator.clipboard.writeText(`${window.location.origin}/${folderName}`)
            addToast({
              type: 'info',
              message: 'Shareable URL copied to clipboard!'
            })
          } catch (error) {
            console.log('Clipboard copy failed')
          }
        }
      } else {
        // Create regular folder only
        await createFolder(folderName.trim(), isPublic)
        addToast({
          type: 'success',
          message: 'Folder created successfully!'
        })
      }
      
      // Refresh file list
      loadItems()
      
      onClose()
      setFolderName('')
      setIsPublic(true)
      setIsShared(false)
      setPassword('')
    } catch (error) {
      console.error('Folder creation failed:', error)
      addToast({
        type: 'error',
        message: 'Failed to create folder'
      })
    } finally {
      setIsCreating(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl bg-dark-800 p-6 shadow-2xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-primary-500/20 p-2">
              <FolderOpen className="h-6 w-6 text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Create Folder</h2>
              <p className="text-dark-400">
                Create in: {currentPath || 'Root folder'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-dark-400 hover:bg-dark-700 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Folder Name */}
          <div>
            <label className="block text-sm font-medium text-dark-200">
              Folder Name
            </label>
            <div className="relative mt-1">
              <input
                type="text"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="Enter folder name"
                className="input pl-12"
                disabled={isCreating}
                autoFocus
                maxLength={50}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <Folder className="h-5 w-5 text-dark-400" />
              </div>
            </div>
          </div>

          {/* Folder Type */}
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-2">
              Folder Type
            </label>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setIsShared(false)}
                disabled={isCreating}
                className={`flex w-full items-center space-x-3 rounded-lg p-3 text-left transition-colors ${
                  !isShared
                    ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/50'
                    : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                }`}
              >
                <div className={`h-3 w-3 rounded-full ${!isShared ? 'bg-blue-400' : 'bg-dark-500'}`} />
                <div>
                  <p className="font-medium">Regular Folder</p>
                  <p className="text-sm opacity-75">Standard folder in your file system</p>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => setIsShared(true)}
                disabled={isCreating}
                className={`flex w-full items-center space-x-3 rounded-lg p-3 text-left transition-colors ${
                  isShared
                    ? 'bg-green-500/20 text-green-400 ring-1 ring-green-500/50'
                    : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                }`}
              >
                <div className={`h-3 w-3 rounded-full ${isShared ? 'bg-green-400' : 'bg-dark-500'}`} />
                <div className="flex items-center gap-2">
                  <div>
                    <p className="font-medium">Shared Folder</p>
                    <p className="text-sm opacity-75">Password-protected with shareable URL</p>
                  </div>
                  <Users className="h-4 w-4 text-green-400" />
                </div>
              </button>
            </div>
          </div>

          {/* Shared Folder Password */}
          {isShared && (
            <div>
              <label className="block text-sm font-medium text-dark-200">
                Folder Password
              </label>
              <div className="relative mt-1">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password for folder access"
                  className="input pl-12"
                  disabled={isCreating}
                  required
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <Lock className="h-5 w-5 text-dark-400" />
                </div>
              </div>
              <p className="text-xs text-dark-400 mt-1">
                Users will need this password to upload/edit/delete files
              </p>
            </div>
          )}

          {/* Regular Folder Privacy */}
          {!isShared && (
            <div>
              <label className="block text-sm font-medium text-dark-200">
                Folder Visibility
              </label>
              <div className="mt-2 space-y-2">
                <button
                  type="button"
                  onClick={() => setIsPublic(true)}
                  disabled={isCreating}
                  className={`flex w-full items-center space-x-3 rounded-lg p-3 text-left transition-colors ${
                    isPublic
                      ? 'bg-green-500/20 text-green-400 ring-1 ring-green-500/50'
                      : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                  }`}
                >
                  <div className={`h-3 w-3 rounded-full ${isPublic ? 'bg-green-400' : 'bg-dark-500'}`} />
                  <div>
                    <p className="font-medium">Public</p>
                    <p className="text-sm opacity-75">Files can be accessed directly via URL</p>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setIsPublic(false)}
                  disabled={isCreating}
                  className={`flex w-full items-center space-x-3 rounded-lg p-3 text-left transition-colors ${
                    !isPublic
                      ? 'bg-red-500/20 text-red-400 ring-1 ring-red-500/50'
                      : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                  }`}
                >
                  <div className={`h-3 w-3 rounded-full ${!isPublic ? 'bg-red-400' : 'bg-dark-500'}`} />
                  <div>
                    <p className="font-medium">Private</p>
                    <p className="text-sm opacity-75">Requires authentication to access</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Shared Folder Info */}
          {isShared && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
              <h4 className="text-sm font-medium text-green-400 mb-2">✨ Shared Folder Features:</h4>
              <ul className="text-xs text-green-300 space-y-1">
                <li>• 🌐 Direct URL: {HOME_URL.replace(/^https?:\/\//, '')}/{folderName || 'folder-name'}</li>
                <li>• 🔒 Password-protected file operations</li>
                <li>• 👀 Public files viewable without password</li>
                <li>• 👑 You have admin access (no password needed)</li>
                <li>• 📁 Appears in your file manager</li>
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isCreating}
              className="btn btn-ghost disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!folderName.trim() || (isShared && !password) || isCreating}
              className="btn btn-primary disabled:opacity-50"
            >
              {isCreating ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Creating...
                </>
              ) : (
                <>
                  <Folder className="mr-2 h-4 w-4" />
                  Create {isShared ? 'Shared' : 'Regular'} Folder
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}